import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ChangeEvent, JSX, useState } from 'react'

import { Button, ButtonWithIcon, Icon, Skeleton, Textarea } from '@writefull/ui'
import { clsx } from '@writefull/utils'

import Container from '@ui/shared/components/Container'
import FAQs, { FAQsProps } from '@ui/shared/components/FAQs'

import AiResult from './AiResult'
import AiResultSkeleton from './AiResultSkeleton'

type ActionButtonProps = React.ComponentProps<typeof Button> & {
  icon: JSX.Element | undefined
}

function ActionButton({ icon, ...props }: ActionButtonProps) {
  if (!icon) {
    return <Button {...props} />
  }
  return <ButtonWithIcon {...props} icon={icon} />
}

interface AiGeneratorProps {
  label: string
  actions: {
    name: string
    code: string
    variant?: ActionButtonProps['variant']
    icon?: JSX.Element
    className?: string
    tooltip?: string
  }[]
  skeleton: {
    type: Parameters<typeof Skeleton>[0]['type']
    bars: number
  }
  faqs: FAQsProps
  examples?: string[]
  minLength?: number
  maxLength?: number
  copyToClipboardOutputConverter?: (output: string) => string
  onGenerate: (input: string, code: string) => Promise<string[] | undefined>
}

export default function AiGenerator({
  label,
  actions,
  skeleton,
  faqs,
  examples = undefined,
  minLength = undefined,
  maxLength = undefined,
  copyToClipboardOutputConverter = undefined,
  onGenerate,
}: AiGeneratorProps) {
  const [input, setInput] = useState('')
  const [currentAction, setCurrentAction] = useState<string | undefined>()
  const [output, setOutput] = useState<undefined | string[]>(undefined)
  const [isGenerating, setIsGenerating] = useState(false)
  const showOutput = output?.length && !isGenerating

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement
    const code = submitter.name

    setIsGenerating(true)
    setCurrentAction(code)
    const output = await onGenerate(input, code)
    setOutput(output)
    setIsGenerating(false)
  }

  return (
    <>
      <Container className='grid lg:grid-cols-2 py-3 md:py-4 gap-4'>
        <form className='flex flex-col gap-2 items-end' onSubmit={handleSubmit}>
          <div className='flex flex-row flex-wrap gap-x-2 gap-y-3 self-start'>
            <label htmlFor='input'>
              {label}
              {examples?.length && (
                <> or try {examples?.length > 1 ? 'these examples' : ' this example'}</>
              )}
              :
            </label>
            {examples?.length && (
              <div className='flex flex-row gap-2 ml-auto'>
                {examples.map((example, idx) => (
                  <button
                    type='button'
                    key={example}
                    className='rounded-full bg-gray-200 hover:bg-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-800'
                    onClick={() => setInput(example)}
                  >
                    #{idx + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
          {maxLength && maxLength > 0 && (
            <div className='w-full flex justify-between gap-2'>
              <div
                className={clsx(
                  'flex items-center gap-1 text-xs transition-opacity text-error',
                  input.length / maxLength >= 1 ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden={input.length / maxLength < 1}
              >
                <Icon icon={<FontAwesomeIcon icon={faExclamationTriangle} size='sm' />} size={3} />
                You have reached the 2,000 character limit.
              </div>
              <div
                className={clsx(
                  'text-xs text-gray-500 transition-colors',
                  input.length > 0 ? 'opacity-100' : 'opacity-0',
                  input.length / maxLength > 0.85 ? 'text-warning' : '',
                  input.length / maxLength >= 1 ? '!text-error' : '',
                )}
              >
                {input.length}/{maxLength}
              </div>
            </div>
          )}

          <Textarea
            id='input'
            name='input'
            value={input}
            rows={8}
            className='resize-y focus:ring-0! theme-transition'
            onChange={handleChange}
            disabled={isGenerating}
            minLength={minLength}
            maxLength={maxLength}
            required
          />
          <div className='self-end flex flex-row gap-2'>
            {actions.map((action) => (
              <ActionButton
                key={action.code}
                name={action.code}
                type='submit'
                variant={action.variant ?? 'primary'}
                size='big'
                className={clsx(
                  'w-fit',
                  action.className ?? '',
                  (output?.length || isGenerating) && currentAction !== action.code
                    ? 'opacity-70'
                    : '',
                )}
                icon={action.icon}
                loading={isGenerating && currentAction === action.code}
                tooltip={action.tooltip}
              >
                {action.name}
              </ActionButton>
            ))}
          </div>
        </form>
        {showOutput && (
          <div className='flex flex-col gap-4'>
            {output.map((result, i) => (
              <AiResult
                key={i}
                outputConverter={copyToClipboardOutputConverter}
                actionCode={currentAction}
              >
                {result}
              </AiResult>
            ))}
          </div>
        )}
        {isGenerating && <AiResultSkeleton {...skeleton} />}
      </Container>
      <FAQs {...faqs} />
    </>
  )
}
